import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { ExpiryChip } from '../components/ExpiryChip.tsx';
import { Modal } from '../components/Modal.tsx';
import {
  orderAdoptionHistory,
  orderFileHistory,
  formatFileSize,
  formatUploader,
  type AdoptionHistoryItem,
  type FileHistoryItem,
} from './fileHistory.ts';
import { submissionStatusLabel } from '../lib/submissionStatus.ts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Every version of the document(s) uploaded against one submission: the live
// file(s) labelled "Current", replaced/removed ones "Replaced", newest-first.
// History (including superseded rows + their download URLs) comes from the
// admin file-history endpoint.
export function FileHistoryModal({
  companyId,
  submissionId,
  questionLabel,
  onClose,
}: {
  companyId: string;
  submissionId: string;
  questionLabel: string;
  onClose: () => void;
}) {
  const historyQuery = useQuery({
    queryKey: ['company', companyId, 'submission', submissionId, 'file-history'],
    queryFn: () =>
      adminApi.get<{
        files: FileHistoryItem[];
        adoptions: AdoptionHistoryItem[];
      }>(`/api/companies/${companyId}/submissions/${submissionId}/files/history`),
  });

  const versions = orderFileHistory(historyQuery.data?.files ?? []);
  const adoptionVersions = orderAdoptionHistory(historyQuery.data?.adoptions ?? []);

  return (
    <Modal
      open
      size="lg"
      title={`History — ${questionLabel}`}
      onClose={onClose}
      footer={
        <Btn variant="secondary" size="sm" onClick={onClose}>
          Close
        </Btn>
      }
    >
      {historyQuery.isLoading ? (
        <CenteredSpinner />
      ) : historyQuery.isError ? (
        <ErrorMessage error={historyQuery.error} />
      ) : versions.length === 0 && adoptionVersions.length === 0 ? (
        <p className="text-sm italic text-slate-400">No files have been uploaded yet.</p>
      ) : (
        <>
        {versions.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {versions.map((v) => {
            const uploadedBy = formatUploader(v.uploaded_by_name, v.uploaded_by_role);
            return (
            <li key={v.id} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {v.file_name}
                  </span>
                  {v.state === 'current' ? (
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Current
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                      Replaced
                    </span>
                  )}
                  {v.state === 'replaced' && v.superseded_status && (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                      {submissionStatusLabel(v.superseded_status)}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span>Uploaded {formatDate(v.created_at)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatFileSize(v.size_bytes)}</span>
                  {uploadedBy && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>By {uploadedBy}</span>
                    </>
                  )}
                  {v.expires_at && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="inline-flex items-center gap-1">
                        Expiry <ExpiryChip expiresAt={v.expires_at} />
                      </span>
                    </>
                  )}
                  {v.state === 'replaced' && v.superseded_at && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>Replaced {formatDate(v.superseded_at)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="shrink-0">
                {v.download_url ? (
                  <a
                    href={v.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#4D148C] hover:underline"
                  >
                    Download
                  </a>
                ) : (
                  <span className="text-xs italic text-slate-400">Unavailable</span>
                )}
              </div>
            </li>
            );
          })}
        </ul>
        )}
        {adoptionVersions.length > 0 && (
          <div className={versions.length > 0 ? 'mt-4' : ''}>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Policy document choice
            </h4>
            <ul className="divide-y divide-slate-100">
              {adoptionVersions.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {a.mode === 'adopted' ? 'Standard policy adopted' : a.file_name}
                      </span>
                      {a.state === 'current' ? (
                        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Current
                        </span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          {a.archived_reason === 'cleared' ? 'Cleared' : 'Replaced'}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      {a.mode === 'adopted' ? (
                        <span>Acknowledged {formatDate(a.acknowledged_at ?? a.recorded_at)}</span>
                      ) : (
                        <span>Uploaded {formatDate(a.recorded_at)}</span>
                      )}
                      {a.archived_at && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span>
                            {a.archived_reason === 'cleared' ? 'Cleared' : 'Replaced'}{' '}
                            {formatDate(a.archived_at)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {a.mode === 'uploaded' &&
                      (a.download_url ? (
                        <a
                          href={a.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#4D148C] hover:underline"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="text-xs italic text-slate-400">Unavailable</span>
                      ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        </>
      )}
    </Modal>
  );
}
